import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useSubscription } from "@/hooks/useSubscription";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, Linkedin, Sparkles, ArrowLeft, Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { toast } from "sonner";
import { SubscriptionGate } from "@/components/SubscriptionGate";
import { DashboardLayout } from "@/components/DashboardLayout";

interface LinkedInProfile {
  id: string;
  optimized_headline: string;
  optimized_summary: string;
  keywords: string[];
  suggestions: string[];
}

const LinkedInOptimizer = () => {
  const navigate = useNavigate();
  const { hasFeature } = useSubscription();
  const [currentHeadline, setCurrentHeadline] = useState("");
  const [currentSummary, setCurrentSummary] = useState("");
  const [targetRole, setTargetRole] = useState("");
  const [optimizing, setOptimizing] = useState(false);
  const [optimizedProfile, setOptimizedProfile] = useState<LinkedInProfile | null>(null);

  const optimizeProfile = async () => {
    if (!currentHeadline.trim() && !currentSummary.trim()) {
      toast.error("Please enter your current headline or summary");
      return;
    }

    setOptimizing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/login");
        return;
      }

      // Generate profile hash for idempotency
      const encoder = new TextEncoder();
      const profileData = `${currentHeadline}:${currentSummary}:${targetRole}`;
      const hashBuffer = await crypto.subtle.digest('SHA-256', encoder.encode(profileData));
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const profileHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('').slice(0, 16);
      const refId = `linkedin:${session.user.id}:${profileHash}:${Date.now()}`;

      // Phase 1: Charge credits atomically
      const { data: creditResult, error: creditError } = await supabase.functions.invoke('credits-spend', {
        body: { featureKey: 'linkedin.optimize', refId }
      });

      if (creditError) {
        const errorData = creditError as any;
        if (errorData.error === 'INSUFFICIENT_CREDITS') {
          toast.error(errorData.message || 'Insufficient credits', {
            action: { label: 'Get Credits', onClick: () => navigate('/pricing') },
          });
          return;
        }
        throw creditError;
      }

      // Phase 2: Execute optimization
      const { data, error } = await supabase.functions.invoke("optimize-linkedin", {
        body: { 
          current_headline: currentHeadline, 
          current_summary: currentSummary,
          target_role: targetRole
        },
      });

      if (error) throw error;

      setOptimizedProfile(data);
      
      if (!creditResult.idempotent && creditResult.cost > 0) {
        toast.success(`Profile optimized! Used ${creditResult.cost} credits. ${creditResult.remaining} remaining.`);
      } else {
        toast.success("Profile optimized successfully!");
      }

      // Refresh credits display
      window.dispatchEvent(new CustomEvent('creditsUpdated'));
    } catch (error) {
      console.error("[LINKEDIN] Error:", error);
      toast.error("Failed to optimize LinkedIn profile");
    } finally {
      setOptimizing(false);
    }
  };

  if (!hasFeature('linkedinOptimizer')) {
    return (
      <DashboardLayout>
        <SubscriptionGate
          feature="LinkedIn Profile Optimizer"
          requiredPlan="pro"
          description="Get AI-powered suggestions to optimize your LinkedIn profile for recruiter visibility and engagement."
        />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-3">
              <Linkedin className="h-6 w-6 text-blue-600" />
              LinkedIn Profile Optimizer
            </h1>
            <p className="text-muted-foreground">
              Transform your LinkedIn profile to attract top recruiters and opportunities
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Form */}
        <Card className="p-6">
          <h2 className="text-2xl font-semibold mb-4">Your Current Profile</h2>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="headline">Current Headline</Label>
              <Input
                id="headline"
                placeholder="e.g., Software Engineer at Acme Corp"
                value={currentHeadline}
                onChange={(e) => setCurrentHeadline(e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">Max 220 characters</p>
            </div>

            <div>
              <Label htmlFor="summary">Current Summary/About Section</Label>
              <Textarea
                id="summary"
                placeholder="Paste your current LinkedIn summary here..."
                value={currentSummary}
                onChange={(e) => setCurrentSummary(e.target.value)}
                rows={8}
                className="resize-none"
              />
            </div>

            <div>
              <Label htmlFor="targetRole">Target Role (Optional)</Label>
              <Input
                id="targetRole"
                placeholder="e.g., Senior Product Manager"
                value={targetRole}
                onChange={(e) => setTargetRole(e.target.value)}
              />
            </div>

            <Button
              onClick={optimizeProfile}
              disabled={optimizing || (!currentHeadline.trim() && !currentSummary.trim())}
              className="w-full"
              size="lg"
            >
              {optimizing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Optimizing...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Optimize My Profile
                </>
              )}
            </Button>
          </div>
        </Card>

        {/* Results */}
        <div className="space-y-6">
          {optimizedProfile ? (
            <>
              <Card className="p-6 border-primary/20 bg-primary/5">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="h-5 w-5 text-primary" />
                  <h2 className="text-2xl font-semibold">Optimized Headline</h2>
                </div>
                <p className="text-lg font-medium mb-4">{optimizedProfile.optimized_headline}</p>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(optimizedProfile.optimized_headline);
                    toast.success("Headline copied!");
                  }}
                >
                  Copy Headline
                </Button>
              </Card>

              <Card className="p-6 border-primary/20 bg-primary/5">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="h-5 w-5 text-primary" />
                  <h2 className="text-2xl font-semibold">Optimized Summary</h2>
                </div>
                <p className="whitespace-pre-wrap mb-4">{optimizedProfile.optimized_summary}</p>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(optimizedProfile.optimized_summary);
                    toast.success("Summary copied!");
                  }}
                >
                  Copy Summary
                </Button>
              </Card>

              {optimizedProfile.keywords && optimizedProfile.keywords.length > 0 && (
                <Card className="p-6">
                  <h3 className="font-semibold mb-3">Key Keywords to Use</h3>
                  <div className="flex flex-wrap gap-2">
                    {optimizedProfile.keywords.map((keyword, i) => (
                      <Badge key={i} variant="secondary">{keyword}</Badge>
                    ))}
                  </div>
                </Card>
              )}

              {optimizedProfile.suggestions && optimizedProfile.suggestions.length > 0 && (
                <Card className="p-6">
                  <h3 className="font-semibold mb-3">Additional Suggestions</h3>
                  <ul className="space-y-2">
                    {optimizedProfile.suggestions.map((suggestion, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-primary mt-1">•</span>
                        <span className="text-sm">{suggestion}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              )}
            </>
          ) : (
            <Card className="p-12 text-center">
              <Linkedin className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Get Started</h3>
              <p className="text-muted-foreground">
                Enter your current LinkedIn profile details to receive AI-powered optimization suggestions
              </p>
            </Card>
          )}
        </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default LinkedInOptimizer;
